'use client';
import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { Menu, X, LogOut, Settings } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { jsnService } from '@/lib/supabase/jsnService';
import type { UserProfile } from '@/lib/supabase/types';

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler, { passive: true });

    const load = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const up = await jsnService.getUserProfileById(user.id);
        setCurrentUser(up);
      }
    };
    load();

    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleLogout = async () => {
    const supabase = createClient();
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const isLoggedIn = !!currentUser;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-md border-b border-border' : 'bg-transparent'}`}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5 no-underline">
          <AppLogo size={46} />
          <div>
            <div className="text-muted-foreground text-xs font-medium leading-tight">Jamuhuri Singles Network</div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <a href="#how-it-works" className="nav-link">How It Works</a>
          <a href="#profiles" className="nav-link">Browse Profiles</a>
          <a href="#membership" className="nav-link">Membership</a>
          <Link href="/safety" className="nav-link">Safety</Link>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn && currentUser ? (
            <div className="flex items-center gap-2">
              <Link href="/discover-page" className="btn-ghost px-4 py-2 rounded-lg text-sm">Go to Discover</Link>
              <Link
                href="/account-settings"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold hover:ring-2 hover:ring-primary/60 transition-all"
                style={{ background: currentUser.avatarColor || '#750000' }}
                title="Account Settings"
              >
                {currentUser.avatarInitials}
              </Link>
              <button onClick={handleLogout} className="btn-ghost px-3 py-2 rounded-lg text-sm flex items-center gap-1.5" title="Sign out">
                <LogOut size={14} />Sign Out
              </button>
            </div>
          ) : (
            <>
              <Link href="/sign-up-login-screen" className="btn-ghost px-4 py-2 rounded-lg text-sm">Sign In</Link>
              <Link href="/sign-up-login-screen" className="btn-primary px-5 py-2 rounded-lg text-sm">Join Free</Link>
            </>
          )}
        </div>

        <button className="md:hidden p-2 text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'}>
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-card border-t border-border px-6 py-4 fade-in">
          <div className="flex flex-col gap-4">
            <a href="#how-it-works" className="nav-link py-1" onClick={() => setMobileOpen(false)}>How It Works</a>
            <a href="#profiles" className="nav-link py-1" onClick={() => setMobileOpen(false)}>Browse Profiles</a>
            <a href="#membership" className="nav-link py-1" onClick={() => setMobileOpen(false)}>Membership</a>
            <Link href="/safety" className="nav-link py-1" onClick={() => setMobileOpen(false)}>Safety</Link>
            <div className="flex gap-3 pt-2 border-t border-border">
              {isLoggedIn && currentUser ? (
                <>
                  <Link href="/discover-page" className="btn-primary px-4 py-2 rounded-lg text-sm flex-1 text-center" onClick={() => setMobileOpen(false)}>Go to Discover</Link>
                  <Link href="/account-settings" className="btn-ghost px-4 py-2 rounded-lg text-sm flex items-center gap-1.5" onClick={() => setMobileOpen(false)}>
                    <Settings size={14} />Settings
                  </Link>
                  <button onClick={handleLogout} className="btn-ghost px-4 py-2 rounded-lg text-sm flex items-center gap-1.5">
                    <LogOut size={14} />Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link href="/sign-up-login-screen" className="btn-outline-red px-4 py-2 rounded-lg text-sm flex-1 text-center" onClick={() => setMobileOpen(false)}>Sign In</Link>
                  <Link href="/sign-up-login-screen" className="btn-primary px-4 py-2 rounded-lg text-sm flex-1 text-center" onClick={() => setMobileOpen(false)}>Join Free</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}